import { z } from 'zod';
import { APPROVAL_PUSH_MODES, type ApprovalPushMode, type ModuleKey, type Severity } from '../enums';
import { addDaysLocal, isLocalTime, localToUtc, toLocalDate, toLocalTime } from '../time';
import type { ApprovalPriority } from './approvals';
import { idSchema } from './common';
import { EVENT_TYPES } from './inbox';

/**
 * Powiadomienia (MVP §14, ARCHITECTURE §9, DECISIONS [P] 4.10: godziny ciszy 21:00–8:00, Critical je przełamuje):
 * `notifications` (dziennik wysyłek per zdarzenie, osoba i kanał), `push_subscriptions` (globalna, subskrypcje Web
 * Push przeglądarek osoby), `user_settings` (globalna: godziny ciszy, push akceptacji). Kanał: Web Push (VAPID, PWA).
 *
 * Reguły: Critical → push; Approval → push tylko przy wysokim priorytecie albo ustawieniu „zawsze”; Normal → Inbox;
 * FYI → historia. Godziny ciszy: push odkładany do ich końca, Critical przełamuje je, gdy osoba to włączyła
 * (`critical_breaks_quiet`, domyślnie tak). Treść na ekranie blokady minimalna (bez nazw klientów, kwot, treści):
 * szczegóły po kliknięciu i zalogowaniu (`pushMessageFor`). Decyzję liczy `decidePush` (czysta funkcja: worker i UI).
 */

export { APPROVAL_PUSH_MODES, type ApprovalPushMode };
export const approvalPushModeSchema = z.enum(APPROVAL_PUSH_MODES);

export const NOTIFICATION_CHANNELS = ['push'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];
export const notificationChannelSchema = z.enum(NOTIFICATION_CHANNELS);

/** `queued` (czeka, także odłożone do końca godzin ciszy: `scheduled_for`), `sent`, `suppressed` (z powodem), `failed`. */
export const NOTIFICATION_STATUSES = ['queued', 'sent', 'suppressed', 'failed'] as const;
export type NotificationStatus = (typeof NOTIFICATION_STATUSES)[number];
export const notificationStatusSchema = z.enum(NOTIFICATION_STATUSES);

/**
 * Kod w `notifications.reason` (etykiety w labels/notifications.ts): powód pominięcia (`suppressed`), odłożenia
 * (`queued` + `quiet_hours`), błędu (`failed`: `send_failed`) albo oznaczenie wysyłki w symulacji (`sent` +
 * `simulated`: adapter fake, nic nie wyszło do dostawcy push).
 */
export const NOTIFICATION_SUPPRESS_REASONS = [
  'push_disabled',
  'severity',
  'setting_off',
  'low_priority',
  'no_subscription',
  'quiet_hours',
  'inbox_done',
  'no_access',
  /** osoba już przeczytała element Inboxu (np. przed końcem godzin ciszy) */
  'inbox_read',
  /** element Inboxu odłożony przez osobę */
  'inbox_snoozed',
  /** akceptacja rozstrzygnięta (już nie czeka) */
  'resolved',
  /** seria: push z tej samej serii (`pushSeriesKey`) wysłany w ostatnich `PUSH_BURST_WINDOW_SEC` */
  'burst',
  /** brak adaptera push w procesie (brak konfiguracji) */
  'not_configured',
  /** wszystkie próby wysyłki nieudane (status `failed`) */
  'send_failed',
  /** wysłane w symulacji (status `sent`, adapter fake) */
  'simulated',
  /** poczta pobrana z historii skrzynki (pierwsze połączenie, „Pobierz starsze maile”) — bez push (17.10.2026) */
  'imported',
] as const;
export type NotificationSuppressReason = (typeof NOTIFICATION_SUPPRESS_REASONS)[number];

/** Ważności, które mogą wysłać push. */
export const PUSH_SEVERITIES = ['critical', 'approval'] as const satisfies readonly Severity[];

/** Ile prób wysyłki push (błędy przejściowe dostawcy); subskrypcja 404/410 jest usuwana od razu. */
export const PUSH_MAX_ATTEMPTS = 3;
/** Po tylu kolejnych błędach subskrypcja jest usuwana (`push_subscriptions.failure_count`). */
export const PUSH_SUBSCRIPTION_MAX_FAILURES = 5;
/** Czas życia powiadomienia u dostawcy push (critical krócej: stary alarm nie ma sensu). */
export const PUSH_TTL_SEC: Readonly<Record<'critical' | 'approval', number>> = { critical: 3600, approval: 12 * 3600 };

/**
 * Zdarzenia, które same proszą o push przy ważności „Zwykłe” (Fala 10: „Powiadom mnie” z automatyzacji — np. poranny
 * brief; reguła wybrała push). Push wg ustawień osoby jak „Pilne”, ale godziny ciszy zawsze odkładają powiadomienie
 * (bez przełamywania ciszy) i treść nie mówi „Pilne”.
 */
export const PUSH_REQUESTED_EVENT_TYPES = ['automation.notify_push'] as const;

export function isPushRequestedEvent(type: string): boolean {
  return (PUSH_REQUESTED_EVENT_TYPES as readonly string[]).includes(type);
}

export const PUSH_REQUESTED_BODY = 'Automatyzacja: sprawdź Inbox';

/**
 * Kategorie push dla zdarzeń „Zwykłe” (DECISIONS 2026-10-17 [P] „Push »że coś wpadło«”): przełączniki w Ustawieniach →
 * Powiadomienia, domyślnie włączone. Push dostaje osoba z elementem Inboxu zdarzenia (odbiorców wybiera `ctx.emit`, np.
 * poczta przypisana do osoby albo do klienta z jej zakresu), w godzinach ciszy odłożony (bez przełamywania), seria
 * (`pushSeriesKey`) najwyżej jeden push na okno. FYI nigdy. Critical i akceptacje — bez zmian (własne ustawienia).
 */
export const PUSH_CATEGORIES = ['mail', 'tasks', 'clientRequests'] as const;
export type PushCategory = (typeof PUSH_CATEGORIES)[number];

/** Pole ustawień osoby dla kategorii. */
export const PUSH_CATEGORY_SETTING = {
  mail: 'pushMail',
  tasks: 'pushTasks',
  clientRequests: 'pushClientRequests',
} as const satisfies Record<PushCategory, string>;

const PUSH_CATEGORY_BY_EVENT_TYPE: Readonly<Record<string, PushCategory>> = {
  [EVENT_TYPES.messageReceived]: 'mail',
  [EVENT_TYPES.taskAssigned]: 'tasks',
  [EVENT_TYPES.taskCommented]: 'tasks',
  [EVENT_TYPES.clientRequestCreated]: 'clientRequests',
  [EVENT_TYPES.clientRequestMessage]: 'clientRequests',
};

/** Kategoria push typu zdarzenia (null = typ bez push przy ważności „Zwykłe”). */
export function pushCategoryOf(type: string): PushCategory | null {
  return PUSH_CATEGORY_BY_EVENT_TYPE[type] ?? null;
}

/** Treść push kategorii (ekran blokady: bez nazw klientów, tytułów i treści — szczegóły po kliknięciu). */
export const PUSH_CATEGORY_BODY: Readonly<Record<string, string>> = {
  [EVENT_TYPES.messageReceived]: 'Nowa wiadomość w Inboxie',
  [EVENT_TYPES.taskAssigned]: 'Nowe zadanie dla Ciebie',
  [EVENT_TYPES.taskCommented]: 'Nowy komentarz do Twojego zadania',
  [EVENT_TYPES.clientRequestCreated]: 'Nowe zgłoszenie od klienta',
  [EVENT_TYPES.clientRequestMessage]: 'Nowa wiadomość od klienta',
};
export const PUSH_CATEGORY_BODY_DEFAULT = 'Nowość w Inboxie';
/**
 * Tryb push poczty (DECISIONS 2026-10-17 [P] „Każda poczta w Inboxie, push tylko o istotnych”): `important`
 * (domyślny) — tylko istotne maile („Zwykłe” i Pilne), `all` — każdy nowy odebrany mail (także „Mniej ważne”),
 * `off` — bez push kategorii „Nowa poczta”. Zgodnie wstecz: `pushMail: true` = „Istotne”, `false` = „Wyłączone”.
 * Poczta pobrana z historii skrzynki nie wysyła push w żadnym trybie.
 */
export const PUSH_MAIL_MODES = ['important', 'all', 'off'] as const;
export type PushMailMode = (typeof PUSH_MAIL_MODES)[number];
export const pushMailModeSchema = z.enum(PUSH_MAIL_MODES);

/** Tryb poczty z ustawień (`pushMail: false` zawsze wyłącza; brak trybu = „Istotne”). */
export function pushMailModeOf(s: { pushMail?: boolean; pushMailMode?: PushMailMode | null }): PushMailMode {
  if (s.pushMail === false) return 'off';
  return s.pushMailMode && s.pushMailMode !== 'off' ? s.pushMailMode : 'important';
}

/** Ograniczenie serii: najwyżej jeden push z tej samej serii (`pushSeriesKey`) na osobę w tym oknie (30 min). */
export const PUSH_BURST_WINDOW_SEC = 30 * 60;
/** Odłożone powiadomienie jest „do wysyłki”, gdy do `scheduled_for` zostało mniej niż tyle (zegar workera). */
export const PUSH_DUE_TOLERANCE_SEC = 60;
/** Najwięcej urządzeń z powiadomieniami na osobę. */
export const PUSH_MAX_DEVICES = 10;
/** Długość odcisku adresu subskrypcji (hex z SHA-256 endpointu): UI rozpoznaje „to urządzenie” bez adresu. */
export const PUSH_FINGERPRINT_LENGTH = 32;
/** Kolejka zadania wysyłki (`ctx.emit` dla critical/approval, odłożenie do końca godzin ciszy). */
export const NOTIFICATIONS_PUSH_QUEUE = 'notifications.push';

// ── Ustawienia (user_settings) ──────────────────────────────────────────────

/** Godzina „HH:MM” (Europe/Warsaw); w bazie CHECK `user_settings_quiet_hours_check`. */
export const quietHourSchema = z.string().refine(isLocalTime, { message: 'Nieprawidłowa godzina' });

export const notificationSettingsSchema = z
  .object({
    /** główny przełącznik push na urządzeniach osoby */
    pushEnabled: z.boolean(),
    /** null + null = bez godzin ciszy */
    quietHoursStart: quietHourSchema.nullable(),
    quietHoursEnd: quietHourSchema.nullable(),
    criticalBreaksQuiet: z.boolean(),
    approvalPush: approvalPushModeSchema,
    /** kategorie push zdarzeń „Zwykłe” (17.10.2026), domyślnie włączone */
    pushMail: z.boolean().default(true),
    /** tryb poczty (17.10.2026): Istotne / Wszystkie / Wyłączone; `pushMail` = tryb ≠ „Wyłączone” */
    pushMailMode: pushMailModeSchema.default('important'),
    pushTasks: z.boolean().default(true),
    pushClientRequests: z.boolean().default(true),
  })
  .superRefine((v, ctx) => {
    if ((v.quietHoursStart === null) !== (v.quietHoursEnd === null)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['quietHoursEnd'], message: 'Podaj początek i koniec ciszy' });
    }
  });
export type NotificationSettings = z.infer<typeof notificationSettingsSchema>;

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  pushEnabled: true,
  quietHoursStart: '21:00',
  quietHoursEnd: '08:00',
  criticalBreaksQuiet: true,
  approvalPush: 'high',
  pushMail: true,
  pushMailMode: 'important',
  pushTasks: true,
  pushClientRequests: true,
};

/**
 * Ustawienia z wiersza `user_settings` (tryb poczty z `push_mail` + `push_mail_all`: wyłączone / istotne /
 * wszystkie). Błędny wiersz = domyślne.
 */
export function notificationSettingsFromRow(row: {
  pushEnabled: boolean;
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
  criticalBreaksQuiet: boolean;
  approvalPush: string;
  pushMail: boolean;
  pushMailAll?: boolean | null;
  pushTasks: boolean;
  pushClientRequests: boolean;
}): NotificationSettings {
  const parsed = notificationSettingsSchema.safeParse({
    pushEnabled: row.pushEnabled,
    quietHoursStart: row.quietHoursStart,
    quietHoursEnd: row.quietHoursEnd,
    criticalBreaksQuiet: row.criticalBreaksQuiet,
    approvalPush: row.approvalPush,
    pushMail: row.pushMail,
    pushMailMode: row.pushMail ? (row.pushMailAll ? 'all' : 'important') : 'off',
    pushTasks: row.pushTasks,
    pushClientRequests: row.pushClientRequests,
  });
  return parsed.success ? parsed.data : { ...DEFAULT_NOTIFICATION_SETTINGS };
}

/**
 * Scalenie zmiany ustawień: `pushMailMode` ustawia też `pushMail`; samo `pushMail` (starsze klienty) = true →
 * „Istotne” (albo dotychczasowe „Wszystkie”), false → „Wyłączone”.
 */
export function mergeNotificationSettings(
  before: NotificationSettings,
  input: z.infer<typeof updateNotificationSettingsInput>,
): NotificationSettings {
  const next: NotificationSettings = { ...before };
  for (const key of Object.keys(input) as (keyof typeof input)[]) {
    const value = input[key];
    if (value !== undefined) Object.assign(next, { [key]: value });
  }
  if (input.pushMailMode !== undefined) {
    next.pushMail = input.pushMailMode !== 'off';
  } else if (input.pushMail !== undefined) {
    const current = pushMailModeOf(before);
    next.pushMailMode = input.pushMail ? (current === 'off' ? 'important' : current) : 'off';
  }
  next.pushMailMode = pushMailModeOf(next);
  return next;
}

export const updateNotificationSettingsInput = z
  .object({
    pushEnabled: z.boolean().optional(),
    quietHoursStart: quietHourSchema.nullable().optional(),
    quietHoursEnd: quietHourSchema.nullable().optional(),
    criticalBreaksQuiet: z.boolean().optional(),
    approvalPush: approvalPushModeSchema.optional(),
    pushMail: z.boolean().optional(),
    pushMailMode: pushMailModeSchema.optional(),
    pushTasks: z.boolean().optional(),
    pushClientRequests: z.boolean().optional(),
  })
  .strict();

/**
 * Godziny ciszy po zmianie (scalone z obecnymi ustawieniami): obie albo żadna, początek ≠ koniec.
 * Błąd z polem formularza albo null.
 */
export function quietHoursProblem(s: Pick<NotificationSettings, 'quietHoursStart' | 'quietHoursEnd'>): {
  path: 'quietHoursStart' | 'quietHoursEnd';
  message: string;
} | null {
  if ((s.quietHoursStart === null) !== (s.quietHoursEnd === null)) {
    return { path: 'quietHoursEnd', message: 'Podaj początek i koniec ciszy' };
  }
  if (s.quietHoursStart !== null && s.quietHoursStart === s.quietHoursEnd) {
    return { path: 'quietHoursEnd', message: 'Koniec ciszy musi być inny niż początek' };
  }
  return null;
}

/**
 * Czy chwila `now` jest w godzinach ciszy (Europe/Warsaw). Przedział półotwarty [start, end); przez północ, gdy
 * start > end (21:00–08:00). start = end albo brak = bez ciszy.
 */
export function isInQuietHours(now: Date, start: string | null, end: string | null): boolean {
  if (!start || !end || start === end) return false;
  const t = toLocalTime(now);
  return start < end ? t >= start && t < end : t >= start || t < end;
}

/** Koniec bieżących godzin ciszy (najbliższe `end` po `now`, Europe/Warsaw; uwzględnia zmianę czasu). */
export function quietHoursEndAfter(now: Date, end: string): Date {
  const today = toLocalDate(now);
  const sameDay = localToUtc(today, end);
  return sameDay.getTime() > now.getTime() ? sameDay : localToUtc(addDaysLocal(today, 1), end);
}

export type PushDecision =
  | { action: 'send' }
  | { action: 'defer'; until: Date; reason: 'quiet_hours' }
  | { action: 'suppress'; reason: NotificationSuppressReason };

/**
 * Decyzja push dla jednej osoby i zdarzenia (bez sprawdzania subskrypcji: brak = `no_subscription` przy wysyłce).
 * `approvalPriority` — priorytet akceptacji, gdy zdarzenie to `approval.requested`.
 */
export function decidePush(input: {
  severity: Severity;
  approvalPriority?: ApprovalPriority | null;
  settings: NotificationSettings;
  now: Date;
  /** zdarzenie prosi o push (`PUSH_REQUESTED_EVENT_TYPES`): „Zwykłe” jak „Pilne”, ale bez przełamywania ciszy */
  requested?: boolean;
  /** kategoria push zdarzenia „Zwykłe” (`pushCategoryOf(type)`): push przy włączonej kategorii, bez przełamywania ciszy */
  category?: PushCategory | null;
}): PushDecision {
  const { severity, settings, now } = input;
  if (!settings.pushEnabled) return { action: 'suppress', reason: 'push_disabled' };
  if (input.category && (severity === 'normal' || severity === 'fyi') && !input.requested) {
    // poczta: tryb Istotne / Wszystkie / Wyłączone (17.10.2026); „Do wiadomości” = „Mniej ważne” tylko w „Wszystkie”
    const mailMode = input.category === 'mail' ? pushMailModeOf(settings) : null;
    const enabled = mailMode ? mailMode !== 'off' : settings[PUSH_CATEGORY_SETTING[input.category]] !== false;
    if (!enabled) return { action: 'suppress', reason: 'setting_off' };
    if (severity === 'fyi' && mailMode !== 'all') return { action: 'suppress', reason: 'severity' };
    if (isInQuietHours(now, settings.quietHoursStart, settings.quietHoursEnd)) {
      return {
        action: 'defer',
        until: quietHoursEndAfter(now, settings.quietHoursEnd as string),
        reason: 'quiet_hours',
      };
    }
    return { action: 'send' };
  }
  if (input.requested && (severity === 'normal' || severity === 'fyi')) {
    if (isInQuietHours(now, settings.quietHoursStart, settings.quietHoursEnd)) {
      return {
        action: 'defer',
        until: quietHoursEndAfter(now, settings.quietHoursEnd as string),
        reason: 'quiet_hours',
      };
    }
    return { action: 'send' };
  }
  if (severity !== 'critical' && severity !== 'approval') return { action: 'suppress', reason: 'severity' };
  if (severity === 'approval') {
    if (settings.approvalPush === 'never') return { action: 'suppress', reason: 'setting_off' };
    if (settings.approvalPush === 'high' && input.approvalPriority !== 'high') {
      return { action: 'suppress', reason: 'low_priority' };
    }
  }
  if (isInQuietHours(now, settings.quietHoursStart, settings.quietHoursEnd)) {
    if (severity === 'critical' && settings.criticalBreaksQuiet) return { action: 'send' };
    return { action: 'defer', until: quietHoursEndAfter(now, settings.quietHoursEnd as string), reason: 'quiet_hours' };
  }
  return { action: 'send' };
}

// ── Treść push (ekran blokady: minimum danych) ──────────────────────────────

/** Tytuł każdego powiadomienia (ekran blokady): nazwa aplikacji, bez treści zdarzenia. */
export const PUSH_TITLE = 'Fravity OS';

export interface PushContent {
  title: string;
  body: string;
  /** ścieżka SPA (bez sesji `RequireAuth` prowadzi przez `/login?next=` i wraca tu po logowaniu) */
  url: string;
  /** zastępowanie powiadomień tego samego zdarzenia (ponowienie nie pokaże drugiego) */
  tag: string;
  /** service worker: alarm zostaje na ekranie do kliknięcia */
  severity?: 'critical' | 'approval';
}

/** Treść ogólna alarmu: najwyżej nazwa modułu (bez klienta, adresu strony, treści maila, kwot). */
const CRITICAL_BODY_BY_MODULE: Partial<Record<ModuleKey, string>> = {
  maintenance: 'Pilne: alarm w Utrzymaniu',
  agents: 'Pilne: alarm w Agentach',
  inbox: 'Pilne: wiadomość w Inboxie',
};
export const PUSH_CRITICAL_BODY_DEFAULT = 'Pilne: sprawdź Inbox';
export const PUSH_APPROVAL_BODY = 'Akcja czeka na akceptację';

/**
 * Bez nazw klientów, treści maili i kwot: tytuł zawsze „Fravity OS”, treść zależy tylko od ważności (i modułu przy
 * alarmie), adres prowadzi do rekordu (Inbox: zdarzenie, Akceptacje: akceptacja — jak `approvalPath`).
 */
export function pushMessageFor(e: {
  eventId: string;
  /** `normal` = zdarzenie z `PUSH_REQUESTED_EVENT_TYPES` (automatyzacja) */
  severity: 'critical' | 'approval' | 'normal';
  approvalId?: string | null;
  module?: ModuleKey | null;
  /** zdarzenie z symulacji (symulowany monitor, symulowana wiadomość): treść zaczyna się od „Symulacja:” */
  simulated?: boolean;
  /** typ zdarzenia „Zwykłe” z kategorią push (`pushCategoryOf`): treść kategorii zamiast „Automatyzacja: …” */
  categoryEventType?: string | null;
}): PushContent {
  if (e.severity === 'normal' && e.categoryEventType && pushCategoryOf(e.categoryEventType)) {
    const body = PUSH_CATEGORY_BODY[e.categoryEventType] ?? PUSH_CATEGORY_BODY_DEFAULT;
    return {
      title: PUSH_TITLE,
      body: e.simulated ? `Symulacja: ${body}` : body,
      url: `/inbox?zdarzenie=${e.eventId}`,
      tag: `event:${e.eventId}`,
    };
  }
  if (e.severity === 'normal') {
    return {
      title: PUSH_TITLE,
      body: e.simulated ? `Symulacja: ${PUSH_REQUESTED_BODY}` : PUSH_REQUESTED_BODY,
      url: `/inbox?zdarzenie=${e.eventId}`,
      tag: `event:${e.eventId}`,
    };
  }
  if (e.severity === 'critical') {
    const body = (e.module && CRITICAL_BODY_BY_MODULE[e.module]) || PUSH_CRITICAL_BODY_DEFAULT;
    return {
      title: PUSH_TITLE,
      body: e.simulated ? `Symulacja: ${body.replace(/^Pilne:\s*/, '')}` : body,
      url: `/inbox?zdarzenie=${e.eventId}`,
      tag: `event:${e.eventId}`,
      severity: 'critical',
    };
  }
  return {
    title: PUSH_TITLE,
    body: PUSH_APPROVAL_BODY,
    url: e.approvalId ? `/akceptacje?id=${e.approvalId}` : '/akceptacje',
    tag: `event:${e.eventId}`,
    severity: 'approval',
  };
}

/** Próbne powiadomienie (Ustawienia → Powiadomienia). */
export const PUSH_TEST_MESSAGE: PushContent = {
  title: PUSH_TITLE,
  body: 'Próbne powiadomienie — działa.',
  url: '/ustawienia/powiadomienia',
  tag: 'test',
};

/**
 * Seria powiadomień (ograniczenie `PUSH_BURST_WINDOW_SEC`): typ zdarzenia + pierwszy powiązany rekord (ta sama
 * akceptacja po edycji, ten sam zasób przy kolejnym alarmie); bez rekordu: `dedup_key` bez ostatniego segmentu
 * (`monitor:<id>:<incydent>` → `monitor:<id>`). Bez rekordu i klucza = brak serii (tylko jeden push na zdarzenie).
 */
export function pushSeriesKey(e: {
  type: string;
  links?: readonly { type: string; id: string }[] | null;
  dedupKey?: string | null;
}): string | null {
  const link = e.links?.[0];
  if (link) return `${e.type}|${link.type}:${link.id}`;
  if (e.dedupKey) {
    const cut = e.dedupKey.lastIndexOf(':');
    return `${e.type}|${cut > 0 ? e.dedupKey.slice(0, cut) : e.dedupKey}`;
  }
  return null;
}

/** Service worker otwiera tylko ścieżki tej aplikacji: zaczyna się od „/”, nie od „//” ani „/\”. */
export function isSafePushPath(url: unknown): url is string {
  return typeof url === 'string' && url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\');
}

// ── Subskrypcje (REST `/api/push/*`) ────────────────────────────────────────

/**
 * Usługi Web Push przeglądarek (audyt końcowy Fali 6, SSRF): Chrome, Android i przeglądarki na Chromium — FCM; Firefox —
 * Mozilla autopush; Safari — Apple; Edge — WNS. Adres subskrypcji spoza listy (np. `https://10.0.0.5:5432/`,
 * `https://169.254.169.254/…`) jest odrzucany przy zapisie i przy wysyłce — serwer nie łączy się z dowolnym hostem.
 */
export const PUSH_SERVICE_HOSTS: readonly string[] = [
  'fcm.googleapis.com',
  'android.googleapis.com',
  'updates.push.services.mozilla.com',
];
export const PUSH_SERVICE_HOST_SUFFIXES: readonly string[] = [
  '.push.services.mozilla.com',
  '.push.apple.com',
  '.notify.windows.com',
];

/** Adres subskrypcji w usłudze push przeglądarki: HTTPS, port 443, bez loginu, host z listy. */
export function isAllowedPushEndpoint(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:' || url.username || url.password) return false;
  if (url.port !== '' && url.port !== '443') return false;
  const host = url.hostname.toLowerCase();
  return PUSH_SERVICE_HOSTS.includes(host) || PUSH_SERVICE_HOST_SUFFIXES.some((s) => host.endsWith(s));
}

export const PUSH_ENDPOINT_MESSAGES = {
  https: 'Adres subskrypcji musi używać HTTPS',
  service: 'Nieobsługiwana usługa powiadomień push (obsługiwane: Chrome, Firefox, Safari, Edge)',
} as const;

/** `PushSubscription.toJSON()` z przeglądarki + nazwa urządzenia. */
export const pushSubscriptionInput = z.object({
  endpoint: z
    .string()
    .trim()
    .max(2048)
    .url({ message: 'Nieprawidłowy adres subskrypcji' })
    .refine((u) => u.startsWith('https://'), { message: PUSH_ENDPOINT_MESSAGES.https })
    .refine((u) => !u.startsWith('https://') || isAllowedPushEndpoint(u), { message: PUSH_ENDPOINT_MESSAGES.service }),
  keys: z.object({
    p256dh: z.string().min(16).max(200),
    auth: z.string().min(8).max(100),
  }),
  deviceLabel: z.string().trim().max(120).optional(),
});
export type PushSubscriptionInput = z.infer<typeof pushSubscriptionInput>;

export const removePushSubscriptionInput = z.object({ id: idSchema });

/** Próbne powiadomienie: na jedno urządzenie (`id`) albo na wszystkie urządzenia osoby. */
export const sendTestPushInput = z.object({ id: idSchema.optional() }).strict();

export interface PushSubscriptionDto {
  id: string;
  deviceLabel: string;
  createdAt: Date;
  lastUsedAt: Date | null;
  /** `PUSH_FINGERPRINT_LENGTH` znaków hex z SHA-256 endpointu (UI porównuje z subskrypcją tej przeglądarki) */
  fingerprint: string;
}

/** Stan kanału push w tej instalacji (bez kluczy prywatnych). */
export interface PushConfigDto {
  /** adapter jest i ma klucz publiczny VAPID (przeglądarka może się zapisać) */
  available: boolean;
  /** adapter fake: nic nie wychodzi do dostawcy push */
  simulated: boolean;
  /** klucz publiczny VAPID (`applicationServerKey`) albo null */
  publicKey: string | null;
}

export interface PushTestResultDto {
  /** urządzenia, na które poszło powiadomienie */
  sent: number;
  /** urządzenia z błędem (przejściowym) */
  failed: number;
  /** subskrypcje wygasłe (404/410) usunięte przy tej próbie */
  removed: number;
  simulated: boolean;
}

export interface NotificationDto {
  id: string;
  eventId: string;
  channel: NotificationChannel;
  status: NotificationStatus;
  reason: NotificationSuppressReason | null;
  scheduledFor: Date | null;
  sentAt: Date | null;
  createdAt: Date;
}
